/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { createFilter } from 'react-select';
import Tooltip from 'components/Tooltip';
import get from 'lodash/get';
import { Label } from 'components/Typography';
import { Container, Value, CaretIcon, CheckIcon, Error } from './styled';
import { getSelectStyles, defaultTheme } from './variants';

// value and onChange should only be passed
// in if formik is not used
const Select = ({
  options,
  variant,
  placeholder,
  components,
  label,
  tooltip,
  tooltipVariant,
  formik,
  value,
  onChange,
  name,
  disabled,
  className
}) => {
  const renderLabel = (
    <Label htmlFor={name} variant={variant}>
      {label}
      {tooltip && <Tooltip variant={tooltipVariant || variant}>{tooltip}</Tooltip>}
      {formik && (
        <>
          {get(formik.touched, name) && get(formik.errors, name) && (
            <Error>{get(formik.errors, name)}</Error>
          )}
          {!get(formik.errors, name) && formik.dirty && <CheckIcon />}
        </>
      )}
    </Label>
  );

  const handleChange = (v) =>
    formik ? formik.setFieldValue(name, v.value) : onChange(v);

  const handleBlur = () =>
    formik && formik.setTouched({ ...formik.touched, [name]: true });

  const selectedValue = formik
    ? options.filter((option) => option.value === get(formik.values, name))
    : options.filter((option) => option.value === value);

  return (
    <Container className={className}>
      {label && renderLabel}
      <ReactSelect
        value={selectedValue}
        filterOption={createFilter({ ignoreAccents: false })}
        onChange={handleChange}
        onBlur={handleBlur}
        options={options}
        components={{
          DropdownIndicator: () => (disabled ? null : <CaretIcon variant={variant} />),
          SingleValue: ({ data }) => {
            return (
              <Value variant={variant} data={data} disabled={disabled}>
                {data.label}
              </Value>
            );
          },
          Placeholder: () => {
            return (
              <Value variant={variant} isPlaceholder>
                {placeholder}
              </Value>
            );
          },
          ...components
        }}
        isDisabled={disabled}
        styles={getSelectStyles(variant, disabled)}
        theme={(theme) => defaultTheme(theme)}
      />
    </Container>
  );
};

Select.propTypes = {
  options: PropTypes.array.isRequired,
  variant: PropTypes.oneOf([
    'block',
    'block-thick',
    'light',
    'dark',
    'global',
    'small',
    'blue',
    'outline'
  ]),
  placeholder: PropTypes.string,
  components: PropTypes.object,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  tooltipVariant: PropTypes.oneOf([
    'block',
    'block-thick',
    'light',
    'dark',
    'global',
    'small',
    'blue',
    'outline',
    'grey'
  ]),
  formik: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

Select.defaultProps = {
  variant: 'dark',
  placeholder: '',
  components: {},
  label: '',
  tooltip: '',
  tooltipVariant: null,
  formik: null,
  value: null,
  onChange: () => {},
  name: '',
  disabled: false,
  className: ''
};

export default Select;
